import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization } from './_finHelpers';

// Interest Rate Calculator — solve for rate given loan/payment/term
export default function InterestRateCalc() {
  const [amount, setAmount] = useState(32000);
  const [years, setYears] = useState(3);
  const [months, setMonths] = useState(0);
  const [monthly, setMonthly] = useState(960);
  const c = useMemo(() => {
    const n = years*12 + months;
    // bisection for r
    let lo = 0, hi = 1;
    for (let i = 0; i < 80; i++) {
      const mid = (lo + hi) / 2;
      const pv = mid === 0 ? monthly * n : monthly * (1 - Math.pow(1+mid, -n)) / mid;
      if (pv > amount) lo = mid; else hi = mid;
    }
    const rMonthly = (lo + hi) / 2;
    const annual = rMonthly * 12 * 100;
    const totalPmt = monthly * n;
    const amort = buildAmortization(amount, annual, n);
    return { annual, totalPmt, totalInterest: totalPmt - amount, amort, n };
  }, [amount, years, months, monthly]);
  return (
    <>
      <CalcShell look="cnet" title="Interest Rate Calculator" subtitle="Solve for the implied interest rate given a loan amount, term and monthly payment."
        about={<><p>This calculator finds the interest rate implied by a loan's principal, term, and monthly payment. Useful when a lender quotes only the payment (not the rate).</p></>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Loan amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
            <div className="field"><label>Loan term</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
                <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
              </div>
            </div>
            <div className="field"><label>Monthly payment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthly} onChange={e=>setMonthly(+e.target.value||0)} className="flex-1"/></div></div>
          </div>
          <div>
            <div className="result-panel mb-4"><div className="result-title">Interest rate</div><div className="result-value" data-testid="ir-rate">{c.annual.toFixed(3)}%</div></div>
            <table className="data-table"><tbody>
              <tr><td>Total of {c.n} monthly payments</td><td className="text-right font-semibold">{fmt(c.totalPmt)}</td></tr>
              <tr><td>Total interest paid</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
            </tbody></table>
            <div className="mt-4"><PieChart parts={[{label:'Principal',value:amount,color:'#2563eb'},{label:'Interest',value:c.totalInterest,color:'#10b981'}]} size={170}/></div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div><h3 className="font-bold text-slate-900 mb-3">Loan Amortization</h3><AmortTable schedule={c.amort}/></div>
          <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={amount}/></div>
        </div>
      </CalcShell>
    </>
  );
}
